import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  IconArrowRight,
  IconBarbell,
  IconChartBar,
  IconCheck,
  IconChevronRight,
  IconClock,
  IconLogout,
  IconPlayerPlay,
  IconPlayerSkipForward,
  IconPlus,
  IconTarget,
  IconTrendingUp,
  IconTrophy,
} from '@tabler/icons-react';
import toast from 'react-hot-toast';

import appStore from '@duck/app-store';
import {
  fetchAthleteCalendar,
  fetchAthleteHome,
  abandonWorkoutSession,
  skipWorkoutSlot,
} from '@util/athleteSession';
import { toErrorMessage } from '@util/toErrorMessage';

import DayActivityModal, {
  entriesForDay,
  ymd,
} from './DayActivityModal';
import { TrophyCaseStrip } from '@components/Athlete/TrophyCase';
import classes from './Home.module.css';

const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function buildCalendarCells(year, month, highlightedDay, workoutDays, overlayDays = []) {
  const first = new Date(year, month, 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  const overlaySet = new Set(overlayDays);

  for (let i = 0; i < startPad; i += 1) {
    cells.push({ key: `pad-${i}`, label: '', muted: true });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({
      key: `day-${day}`,
      day,
      label: String(day),
      muted: false,
      today: day === highlightedDay,
      workout: workoutDays.includes(day),
      overlay: overlaySet.has(day),
    });
  }

  return cells;
}

function formatNow() {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
    .format(new Date())
    .toUpperCase()
    .replace(',', ' |');
}

function formatSignedDelta(value, suffix = '') {
  const number = Number(value) || 0;
  if (number === 0) return `0${suffix}`;
  const sign = number > 0 ? '+' : '−';
  return `${sign}${Math.abs(number)}${suffix}`;
}

export default function AthleteHome() {
  const navigate = useNavigate();
  const athlete = appStore((state) => state.athlete);
  const [home, setHome] = useState(null);
  const [loading, setLoading] = useState(true);
  const [skipping, setSkipping] = useState(false);
  const [modalDate, setModalDate] = useState(null);
  const [modalEntries, setModalEntries] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [openSessionPrompt, setOpenSessionPrompt] = useState(null);
  const [resumeBusy, setResumeBusy] = useState(false);
  const modalRequestRef = useRef(0);

  function applyHomeSummary(summary) {
    setHome(summary);
    if (summary?.open_workout_session?.id) {
      setOpenSessionPrompt(summary.open_workout_session);
    } else {
      setOpenSessionPrompt(null);
    }
  }

  function loadHome() {
    setLoading(true);
    return fetchAthleteHome()
      .then((summary) => {
        applyHomeSummary(summary);
      })
      .catch((error) => {
        toast.error(toErrorMessage(error));
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchAthleteHome()
      .then((summary) => {
        if (!cancelled) applyHomeSummary(summary);
      })
      .catch((error) => {
        if (!cancelled) toast.error(toErrorMessage(error));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function onResumeOpenSession() {
    const open = openSessionPrompt;
    if (!open || resumeBusy) return;
    setOpenSessionPrompt(null);
    if (open.source === 'custom') {
      navigate('/workout', {
        state: {
          source: 'custom',
          workoutSessionId: open.workout_session_id || open.id,
        },
      });
      return;
    }
    if (open.source === 'overlay') {
      navigate('/workout', {
        state: {
          source: 'overlay',
          id: open.athlete_calendar_event_id,
          onDate: open.on_date || open.performed_on || undefined,
        },
      });
      return;
    }
    navigate('/workout', {
      state: {
        source: 'slot',
        id: open.workout_slot_id,
      },
    });
  }

  function onStartCustomWorkout() {
    navigate('/workout', { state: { source: 'custom' } });
  }

  async function onEndOpenSession() {
    const open = openSessionPrompt;
    if (!open?.id || resumeBusy) return;
    setResumeBusy(true);
    try {
      await abandonWorkoutSession(open.id);
      setOpenSessionPrompt(null);
      toast.success('Workout ended');
      await loadHome();
    } catch (error) {
      toast.error(toErrorMessage(error));
    } finally {
      setResumeBusy(false);
    }
  }

  const profile = home?.profile;
  const displayName =
    (athlete?.full_name && athlete.full_name.split(' ')[0]) ||
    profile?.first_name ||
    'Athlete';

  const calendarCells = useMemo(() => {
    if (!home?.calendar) return [];
    return buildCalendarCells(
      home.calendar.year,
      home.calendar.month,
      home.calendar.highlighted_day,
      home.calendar.workout_days || [],
      home.calendar.overlay_days || [],
    );
  }, [home?.calendar]);

  const todayKey = useMemo(() => ymd(new Date()), []);

  const modalDateKey = modalDate ? ymd(modalDate) : null;
  const modalDateLabel = modalDate
    ? modalDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
      })
    : '';
  const dayEntries = useMemo(
    () => (modalDateKey ? entriesForDay(modalEntries, modalDateKey) : []),
    [modalEntries, modalDateKey],
  );

  function openDayModal(day) {
    const calendar = home?.calendar;
    if (!calendar) return;
    const date = new Date(calendar.year, calendar.month, day);
    const requestId = modalRequestRef.current + 1;
    modalRequestRef.current = requestId;
    setModalDate(date);
    setModalEntries([]);
    setModalLoading(true);
    const fromDate = ymd(new Date(calendar.year, calendar.month, 1));
    const toDate = ymd(new Date(calendar.year, calendar.month + 1, 0));
    fetchAthleteCalendar({ fromDate, toDate })
      .then((payload) => {
        if (modalRequestRef.current !== requestId) return;
        setModalEntries(payload.data || []);
      })
      .catch((error) => {
        if (modalRequestRef.current !== requestId) return;
        setModalEntries([]);
        toast.error(toErrorMessage(error));
      })
      .finally(() => {
        if (modalRequestRef.current !== requestId) return;
        setModalLoading(false);
      });
  }

  function closeDayModal() {
    modalRequestRef.current += 1;
    setModalDate(null);
    setModalEntries([]);
    setModalLoading(false);
  }

  function onLogout() {
    axios
      .post('/logout/')
      .then(() => {
        window.location = '/app/athlete';
      })
      .catch(() => {
        window.location = '/app/athlete';
      });
  }

  function onViewProgram() {
    toast('Program view coming next');
  }

  function onViewAllExercises() {
    toast('Full exercise list coming next');
  }

  function onSkipWorkout() {
    const slotId = home?.today_workout?.workout_slot_id;
    if (!slotId || skipping) return;
    if (
      !window.confirm(
        'Skip today’s workout? The next lift will move into today.',
      )
    ) {
      return;
    }

    setSkipping(true);
    skipWorkoutSlot(slotId)
      .then((payload) => {
        if (payload?.rest) {
          toast.success('Workout skipped — rest day now');
        } else {
          toast.success('Workout skipped — next lift is ready today');
        }
        return loadHome();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setSkipping(false));
  }

  if (loading) {
    return (
      <div className={classes.page}>
        <p className={classes.welcomeEyebrow}>Loading…</p>
      </div>
    );
  }

  if (!home || !profile) {
    return (
      <div className={classes.page}>
        <header className={classes.topBar}>
          <div>
            <p className={classes.welcomeEyebrow}>Welcome back,</p>
            <h1 className={classes.welcomeName}>{displayName}</h1>
          </div>
          <button type="button" className={classes.logoutBtn} onClick={onLogout}>
            <IconLogout size={16} stroke={2} />
            Log Out
          </button>
        </header>
        <section className={classes.card}>
          <p className={classes.heroBody}>Could not load athlete home.</p>
        </section>
      </div>
    );
  }

  const todayWorkout = home.today_workout;
  const exercisesOnTap = home.exercises_on_tap || [];
  const stats30d = home.stats_30d || {};
  const weekLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const jsDow = new Date().getDay();
  const todayWeekLabel = jsDow === 0 ? 'Sun' : weekLabels[jsDow - 1];
  const todayWeekRow = (home.this_week || []).find(
    (row) => row.day === todayWeekLabel,
  );
  const doneForToday =
    !todayWorkout && todayWeekRow?.status === 'completed';

  const stats = [
    {
      label: 'Workouts',
      value: stats30d.workouts?.value ?? 0,
      delta: formatSignedDelta(stats30d.workouts?.delta_pct, '%'),
      Icon: IconBarbell,
    },
    {
      label: 'Training Time',
      value: stats30d.training_time?.value ?? '—',
      delta: formatSignedDelta(stats30d.training_time?.delta_pct, '%'),
      Icon: IconClock,
    },
    {
      label: 'Total Volume',
      value: stats30d.total_volume?.value ?? '—',
      delta: formatSignedDelta(stats30d.total_volume?.delta_pct, '%'),
      Icon: IconChartBar,
    },
    {
      label: 'PRs',
      value: stats30d.prs?.value ?? 0,
      delta: formatSignedDelta(stats30d.prs?.delta_abs),
      Icon: IconTrophy,
    },
  ];

  return (
    <div className={classes.page}>
      <header className={classes.topBar}>
        <div>
          <p className={classes.welcomeEyebrow}>Welcome back,</p>
          <h1 className={classes.welcomeName}>{displayName}</h1>
          <p className={classes.profileLine}>
            {profile.sport} | {profile.position} | {profile.bodyweight}{' '}
            {(profile.unit || '').toUpperCase()}
          </p>
        </div>
        <div className={classes.topMeta}>
          <span className={classes.dateTime}>{formatNow()}</span>
          <button type="button" className={classes.logoutBtn} onClick={onLogout}>
            <IconLogout size={16} stroke={2} />
            Log Out
          </button>
        </div>
      </header>

      <div className={classes.layout}>
        <div className={classes.mainCol}>
          <section className={classes.card}>
            <div className={classes.cardHeader}>
              <div>
                <p className={classes.cardEyebrow}>Today&apos;s Workout</p>
                {todayWorkout ? (
                  <>
                    <h2 className={classes.cardTitle}>
                      {todayWorkout.program_name}
                    </h2>
                    <p className={classes.cardSub}>
                      {todayWorkout.session_label}
                    </p>
                  </>
                ) : doneForToday ? (
                  <>
                    <h2 className={classes.cardTitle}>Done for today</h2>
                    <p className={classes.cardSub}>
                      {todayWeekRow?.label || 'Workout complete'}
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className={classes.cardTitle}>Rest Day</h2>
                    <p className={classes.cardSub}>No lift scheduled today</p>
                  </>
                )}
              </div>
              {todayWorkout ? (
                <button
                  type="button"
                  className={classes.ghostBtn}
                  onClick={onViewProgram}
                >
                  View Program
                </button>
              ) : null}
            </div>
            {todayWorkout ? (
              <>
                <p className={classes.heroBody}>{todayWorkout.description}</p>
                <div className={classes.metaRow}>
                  <div className={classes.metaItem}>
                    <span className={classes.metaIcon}>
                      <IconBarbell size={18} stroke={1.75} />
                    </span>
                    {todayWorkout.exercise_count} Exercises
                  </div>
                  <div className={classes.metaItem}>
                    <span className={classes.metaIcon}>
                      <IconClock size={18} stroke={1.75} />
                    </span>
                    ~{todayWorkout.estimated_minutes} min Estimated Time
                  </div>
                  <div className={classes.metaItem}>
                    <span className={classes.metaIcon}>
                      <IconTarget size={18} stroke={1.75} />
                    </span>
                    {todayWorkout.primary_focus} Primary Focus
                  </div>
                </div>
                <div className={classes.heroActions}>
                  <button
                    type="button"
                    className={classes.startBtn}
                    onClick={() => {
                      if (todayWorkout?.workout_slot_id) {
                        navigate('/workout', {
                          state: {
                            source: 'slot',
                            id: todayWorkout.workout_slot_id,
                          },
                        });
                      } else if (todayWorkout?.athlete_calendar_event_id) {
                        navigate('/workout', {
                          state: {
                            source: 'overlay',
                            id: todayWorkout.athlete_calendar_event_id,
                          },
                        });
                      } else {
                        navigate('/workout');
                      }
                    }}
                  >
                    <IconPlayerPlay size={18} stroke={2.5} />
                    Start Today&apos;s Workout
                    <IconArrowRight size={18} stroke={2.5} />
                  </button>
                  {todayWorkout?.workout_slot_id ||
                  todayWorkout?.source === 'slot' ? (
                    <button
                      type="button"
                      className={classes.skipWorkoutBtn}
                      onClick={onSkipWorkout}
                      disabled={skipping}
                    >
                      <IconPlayerSkipForward size={18} stroke={2.5} />
                      {skipping ? 'Skipping…' : 'Skip Workout'}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className={classes.skipWorkoutBtn}
                    onClick={onStartCustomWorkout}
                  >
                    <IconPlus size={18} stroke={2.5} />
                    Custom Workout
                  </button>
                </div>
              </>
            ) : (
              <div className={classes.heroActions}>
                <p className={classes.heroBody}>
                  {doneForToday
                    ? 'Suggested work is done. Start a custom session if you still want to lift.'
                    : 'Enjoy the recovery — or start a custom session if you feel like training.'}
                </p>
                <button
                  type="button"
                  className={classes.startBtn}
                  onClick={onStartCustomWorkout}
                >
                  <IconPlus size={18} stroke={2.5} />
                  Custom Workout
                  <IconArrowRight size={18} stroke={2.5} />
                </button>
              </div>
            )}
          </section>

          <section className={classes.card}>
            <div className={classes.sectionHeader}>
              <p className={classes.sectionLabel}>
                Exercises on Tap ({exercisesOnTap.length})
              </p>
              {exercisesOnTap.length ? (
                <button
                  type="button"
                  className={classes.linkBtn}
                  onClick={onViewAllExercises}
                >
                  View All
                </button>
              ) : null}
            </div>
            {exercisesOnTap.length ? (
              <ul className={classes.exerciseList}>
                {exercisesOnTap.map((exercise) => (
                  <li key={exercise.position} className={classes.exerciseRow}>
                    <span className={classes.exerciseNum}>
                      {exercise.position}
                    </span>
                    <span className={classes.exerciseName}>{exercise.name}</span>
                    <span className={classes.exerciseRx}>
                      {exercise.prescription}
                    </span>
                    <IconChevronRight
                      size={18}
                      className={classes.chevron}
                      stroke={1.75}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <p className={classes.heroBody}>No exercises queued for today.</p>
            )}
          </section>

          <section className={classes.card}>
            <p className={classes.sectionLabel}>Athlete Stats (Last 30 Days)</p>
            <div className={classes.statsGrid}>
              {stats.map((stat) => {
                const StatIcon = stat.Icon;
                return (
                  <div key={stat.label} className={classes.statCard}>
                    <div className={classes.statTop}>
                      <p className={classes.statLabel}>{stat.label}</p>
                      <span className={classes.statIcon}>
                        <StatIcon size={16} stroke={1.75} />
                      </span>
                    </div>
                    <p className={classes.statValue}>{stat.value}</p>
                    <div className={classes.statDelta}>
                      <IconTrendingUp size={14} stroke={2.5} />
                      {stat.delta} vs previous 30 days
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        <aside className={classes.sideCol}>
          <section className={classes.card}>
            <p className={classes.sectionLabel}>
              {home.calendar?.month_label || 'Calendar'}
            </p>
            <div className={classes.calendarGrid}>
              {DOW.map((label, index) => (
                <div key={`${label}-${index}`} className={classes.calDow}>
                  {label}
                </div>
              ))}
              {calendarCells.map((cell) =>
                cell.muted ? (
                  <div
                    key={cell.key}
                    className={`${classes.calDay} ${classes.calDayMuted}`}
                  />
                ) : (
                  <button
                    key={cell.key}
                    type="button"
                    className={[
                      classes.calDay,
                      classes.calDayButton,
                      cell.workout ? classes.calDayWorkout : '',
                      cell.overlay && !cell.workout
                        ? classes.calDayOverlay
                        : '',
                      cell.overlay && cell.workout ? classes.calDayBoth : '',
                      cell.today ? classes.calDayToday : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => openDayModal(cell.day)}
                    aria-label={`Open ${cell.label}`}
                  >
                    <span className={classes.calDayNum}>{cell.label}</span>
                    {cell.overlay ? (
                      <span className={classes.calOverlayDot} aria-hidden />
                    ) : null}
                  </button>
                ),
              )}
            </div>
          </section>

          <section className={classes.card}>
            <p className={classes.sectionLabel}>This Week</p>
            <ul className={classes.weekList}>
              {(home.this_week || []).map((row) => (
                <li
                  key={row.day}
                  className={`${classes.weekRow}${
                    row.status === 'today' ? ` ${classes.weekToday}` : ''
                  }`}
                >
                  <span
                    className={[
                      classes.weekMark,
                      row.status === 'completed' ? classes.weekDone : '',
                      row.status === 'today' ? classes.weekNow : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {row.status === 'completed' ? (
                      <IconCheck size={12} stroke={3} />
                    ) : null}
                  </span>
                  <span className={classes.weekDay}>{row.day}</span>
                  <span className={classes.weekLabel}>{row.label}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className={classes.card}>
            <p className={classes.sectionLabel}>Trophy Case</p>
            <TrophyCaseStrip
              awards={home.recent_awards || []}
              onViewAll={() => navigate('/progress')}
            />
          </section>

          <section className={classes.card}>
            <p className={classes.sectionLabel}>Latest Activity</p>
            <ul className={classes.activityList}>
              {(home.latest_activity || []).map((item) => (
                <li
                  key={`${item.date_label}-${item.name}`}
                  className={classes.activityRow}
                >
                  <span className={classes.activityDate}>{item.date_label}</span>
                  <span className={classes.activityName}>{item.name}</span>
                  <IconCheck size={16} className={classes.check} stroke={2.5} />
                </li>
              ))}
            </ul>
          </section>
        </aside>
      </div>

      <DayActivityModal
        opened={Boolean(modalDate)}
        onClose={closeDayModal}
        dateLabel={modalDateLabel}
        entries={dayEntries}
        loading={modalLoading}
        isToday={modalDateKey === todayKey}
        onLoadWorkout={(target) => {
          closeDayModal();
          navigate('/workout', {
            state: {
              source: target.source,
              id: target.id,
              onDate: target.onDate,
            },
          });
        }}
      />

      {openSessionPrompt ? (
        <div className={classes.confirmOverlay} role="presentation">
          <div
            className={classes.confirmDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="resume-session-title"
          >
            <p id="resume-session-title" className={classes.confirmTitle}>
              You&apos;re in the middle of a workout
            </p>
            <p className={classes.confirmBody}>
              {openSessionPrompt.program_name || 'Workout'}
              {openSessionPrompt.session_label
                ? ` · ${openSessionPrompt.session_label}`
                : ''}
              . Resume where you left off, or end it and move on.
            </p>
            <div className={classes.confirmActions}>
              <button
                type="button"
                className={classes.confirmSecondary}
                onClick={onEndOpenSession}
                disabled={resumeBusy}
              >
                {resumeBusy ? 'Ending…' : 'End & move on'}
              </button>
              <button
                type="button"
                className={classes.confirmPrimary}
                onClick={onResumeOpenSession}
                disabled={resumeBusy}
              >
                Resume workout
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
